import React, { useContext, useMemo } from 'react';

import { faHandshake } from '@fortawesome/free-solid-svg-icons/faHandshake';
import { faHome } from '@fortawesome/free-solid-svg-icons/faHome';
import { faMicrophone } from '@fortawesome/free-solid-svg-icons/faMicrophone';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

import { NavigationContext } from 'buyside/components/presentation/MainSiteLayout/mainSiteLayout';
import ScrollShadow from 'buyside/components/presentation/ScrollShadow/scrollShadow';
import SideNavigationActions from 'buyside/components/presentation/MainSiteNavigation/SideNavigation/SideNavigationActions/sideNavigationActions';
import SideNavigationDropdown from 'buyside/components/presentation/MainSiteNavigation/SideNavigation/SideNavigationDropdown/sideNavigationDropdown';
import SideNavigationItem from 'buyside/components/presentation/MainSiteNavigation/SideNavigation/SideNavigationDropdown/sideNavigationItem';
import SiteWideModal from 'buyside/components/presentation/SiteWideModal/siteWideModal';
import { ActivityFeedJoyrideTarget } from 'buyside/components/presentation/Joyride/ActivityFeedJoyride/activityFeedJoyrideHelper';

import {
  accreditedInvestorProfilesIndexUrl,
  allocatorProfilesUrl,
  compensationsUrl,
  contestsUrl,
  employerProfilesUrl,
  fundLetterIndexUrl,
  ideasUrl,
  // insiderTradesPageUrl,
  jobVaultPostingsUrl,
  podcastEpisodeIndexUrl,
  postsUrl,
  quickPositionsPageUrl,
  rankingsUrl,
  sectorChatsUrl,
  usersUrl,
  videoIndexUrl,
  whitePaperIndexUrl
} from 'buyside/util/urlHelper';
import { getActiveSection } from 'buyside/util/mainSiteNavigationPathHelper';

import { MainNavigationCurrentUserQuery, Maybe } from 'interfaces/graphql-typings';

import BriefcaseIcon from 'buyside/svgs/briefcase.svg';
import DollarSignIcon from 'buyside/svgs/dollar_sign.svg';
import FilePlayIcon from 'buyside/svgs/file_play.svg';
import LightBulbIcon from 'buyside/svgs/lightbulb.svg';
import PencilIcon from 'buyside/svgs/pencil.svg';
import PieChartIcon from 'buyside/svgs/pie_chart.svg';
import PodiumIcon from 'buyside/svgs/podium.svg';
import TrophyIcon from 'buyside/svgs/trophy.svg';
import UserIcon from 'buyside/svgs/user.svg';
import WhitePaper from 'buyside/svgs/macro_wp.svg';
import LettersIcon from 'buyside/svgs/paper_stack.svg';
import MessageBubbleDoubleIcon from 'buyside/svgs/message_bubble_double.svg';

import styles from './sideNavigationStyles.scss';

const SideNavigation: React.FC<{
  currentUser: Maybe<MainNavigationCurrentUserQuery['currentUser']>;
}> = ({ currentUser }) => {
  const { sideNavigationOpen } = useContext(NavigationContext) || {};

  const activeSection = getActiveSection();

  const sectionInfo = useMemo(
    () => ({
      researchSections: [
        {
          name: 'Ideas',
          analyticsTag: 'ideas',
          icon: LightBulbIcon,
          url: ideasUrl()
        },
        {
          name: 'Contests',
          analyticsTag: 'contests',
          disabled: !currentUser,
          icon: TrophyIcon,
          url: contestsUrl()
        },
        {
          name: 'Macro / Thematic',
          analyticsTag: 'Macro / Thematic',
          disabled: !currentUser,
          icon: WhitePaper,
          url: whitePaperIndexUrl()
        },
        {
          name: 'Insights',
          analyticsTag: 'posts',
          disabled: !currentUser,
          icon: PencilIcon,
          url: postsUrl()
        },
        {
          name: 'Fund Letters',
          analyticsTag: 'fund letters',
          icon: LettersIcon,
          url: fundLetterIndexUrl()
        }
      ],
      membersSections: [
        {
          name: 'Profiles',
          analyticsTag: 'profiles',
          disabled: !currentUser?.canAccessUserDetails,
          icon: UserIcon,
          url: usersUrl()
        },
        {
          name: 'Rankings',
          analyticsTag: 'rankings',
          icon: PodiumIcon,
          url: rankingsUrl()
        }
      ],
      mediaSections: [
        {
          name: 'Podcast Episodes',
          analyticsTag: 'podcast episodes',
          icon: (): React.ReactElement<FontAwesomeIconProps> => (
            <FontAwesomeIcon icon={faMicrophone} />
          ),
          url: podcastEpisodeIndexUrl()
        },
        {
          name: 'Videos',
          analyticsTag: 'videos',
          icon: FilePlayIcon,
          url: videoIndexUrl()
        }
      ],
      capIntroSections: [
        {
          name: 'Funds',
          analyticsTag: 'funds',
          disabled: Boolean(!currentUser || currentUser?.isRetail),
          icon: BriefcaseIcon,
          url: employerProfilesUrl()
        },
        {
          name: 'Allocators',
          analyticsTag: 'allocators',
          disabled: Boolean(!currentUser || currentUser?.isRetail),
          icon: PieChartIcon,
          url: allocatorProfilesUrl()
        },
        {
          name: 'Accredited Investors',
          analyticsTag: 'accredited-investors',
          disabled: Boolean(!currentUser || currentUser?.isRetail),
          icon: UserIcon,
          url: accreditedInvestorProfilesIndexUrl()
        }
      ],
      careersSections: [
        {
          name: 'Job Vault',
          analyticsTag: 'job-vault',
          disabled: !currentUser?.canAccessJobPostings,
          icon: BriefcaseIcon,
          url: jobVaultPostingsUrl()
        },
        {
          name: 'Compensation',
          analyticsTag: 'compensation',
          disabled: !currentUser?.canAccessCompensations,
          icon: DollarSignIcon,
          url: compensationsUrl()
        }
      ],
      // secFilingsSections: [
      //   {
      //     name: 'Insider Trades',
      //     analyticsTag: 'insider-trades',
      //     icon: BriefcaseIcon,
      //     url: insiderTradesPageUrl()
      //   }
      // ],
      communityChatsSections: [
        {
          name: 'Sector Chats',
          analyticsTag: 'sector-chats',
          disabled: !currentUser,
          icon: MessageBubbleDoubleIcon,
          url: sectorChatsUrl()
        },
        {
          name: 'Quick Position Chats',
          analyticsTag: 'quick-positions-chats',
          disabled: !currentUser,
          icon: MessageBubbleDoubleIcon,
          url: quickPositionsPageUrl()
        }
      ]
    }),
    [currentUser]
  );

  return (
    <div
      className={classNames(styles.sideNavigation, {
        [styles.sideNavigationOpen]: Boolean(sideNavigationOpen)
      })}
    >
      <ScrollShadow className={styles.navigationItems} hasDarkBackground maxIntensity={0.5}>
        <SideNavigationItem
          name="Home"
          active={activeSection === 'home'}
          analyticsTag="home"
          collapsedIcon={<FontAwesomeIcon icon={faHome} />}
          url={!currentUser ? '/' : '/pro'}
        />
        <SideNavigationDropdown
          name="Research"
          active={activeSection === 'research'}
          analyticsTag="research"
          dropdownItems={sectionInfo.researchSections}
          joyrideTarget={ActivityFeedJoyrideTarget.RESEARCH_TAB}
        />
        <SideNavigationDropdown
          name="Members"
          active={activeSection === 'members'}
          analyticsTag="members"
          dropdownItems={sectionInfo.membersSections}
          joyrideTarget={ActivityFeedJoyrideTarget.MEMBERS_TAB}
        />
        <SideNavigationDropdown
          name="Media"
          active={activeSection === 'media'}
          analyticsTag="media"
          dropdownItems={sectionInfo.mediaSections}
        />
        <SideNavigationDropdown
          name="Cap Intro"
          active={activeSection === 'capIntro'}
          analyticsTag="cap-intro"
          dropdownItems={sectionInfo.capIntroSections}
        />
        <SideNavigationDropdown
          name="Careers"
          active={activeSection === 'careers'}
          analyticsTag="careers"
          dropdownItems={sectionInfo.careersSections}
        />
        {/* <SideNavigationDropdown
          name="SEC Filings"
          active={activeSection === 'secFilings'}
          dropdownItems={sectionInfo.secFilingsSections}
        /> */}
        <SideNavigationDropdown
          name="Community Chats"
          active={activeSection === 'communityChats'}
          dropdownItems={sectionInfo.communityChatsSections}
        />
        <SideNavigationItem
          name="Partner Programs"
          active={activeSection === 'partnerOffers'}
          collapsedIcon={<FontAwesomeIcon icon={faHandshake} />}
          url="/pro/partner-offers"
        />
      </ScrollShadow>
      {currentUser ? (
        <>
          <SideNavigationActions
            canViewIdeas={Boolean(currentUser.canViewIdeas)}
            canWriteIdeas={Boolean(currentUser.canWriteIdeas)}
            currentUserId={currentUser.id}
          />
          {currentUser.canWriteIdeas ? <SiteWideModal /> : null}
        </>
      ) : null}
    </div>
  );
};

export default SideNavigation;
